import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { httpsify } from '../utils/url';
import { loadYouTubeApi } from './AudioTracks';

// Speed options offered for every media-asset video. Native browser menus top
// out at 2x, so we drive video.playbackRate ourselves to allow fast review of
// long clips.
const SPEEDS = [0.5, 1, 2, 3, 4, 5, 6, 8];
const fmtSpeed = (s) => String(s);

// Base hls.js buffering. These are far more generous than the library defaults
// (30s / 60MB) because fast playback (e.g. 6x) drains the forward buffer 6x
// faster in wall-clock time, which causes repeated stalls. We also grow the
// look-ahead with the chosen speed at runtime, and let the user opt into
// preloading the whole clip for jank-free scrubbing/fast review.
const HLS_BASE = {
  enableWorker: true,
  lowLatencyMode: false,
  maxBufferLength: 60,
  maxMaxBufferLength: 600,
  maxBufferSize: 200 * 1000 * 1000,
  backBufferLength: 30,
};

// Supports adaptive HLS (.m3u8) via hls.js, with native fallback for Safari
// and plain progressive MP4.
//
// `getFreshSrc` (optional, async () => url): for progressive MP4 served from
// presigned S3 URLs (raw footage), the link expires after a few hours. If a
// range request fails or stalls because the URL went stale, we transparently
// re-sign and resume at the same spot. CDN-public HLS never expires, so this is
// skipped there.
export default function VideoPlayer({ src, type, poster, getFreshSrc, fps, song, trueSpeed }) {
  const ref = useRef(null);
  const hlsRef = useRef(null);
  const songAudioRef = useRef(null);
  const songYtRef = useRef(null);
  const [speed, setSpeed] = useState(1);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [preload, setPreload] = useState(false);
  const [buffered, setBuffered] = useState(0); // 0..1 of duration buffered ahead
  const speedRef = useRef(1);
  const preloadRef = useRef(false);
  const fpsRef = useRef(Number(fps) > 0 ? Number(fps) : 30);
  const speedBoxRef = useRef(null);
  // HLS renditions (e.g. a RED proxy ships 1080p + 4K). We default to the best
  // level at or below 1080p so playback starts fast, and let the viewer pick a
  // higher one (which then has to buffer more).
  const [levels, setLevels] = useState([]); // [{ index, height, label }]
  const [curLevel, setCurLevel] = useState(-1); // -1 = auto, else level index
  const [qualOpen, setQualOpen] = useState(false);
  const qualBoxRef = useRef(null);

  useEffect(() => {
    fpsRef.current = Number(fps) > 0 ? Number(fps) : 30;
  }, [fps]);

  const isHls = type === 'hls' || (src && src.endsWith('.m3u8'));

  // Push the current speed/preload choice into the live hls.js buffer config.
  // Changing config on the running instance is picked up by its buffer loop.
  const applyBufferTarget = () => {
    const hls = hlsRef.current;
    if (!hls) return;
    if (preloadRef.current) {
      // Effectively "buffer the entire clip ahead of playback".
      hls.config.maxBufferLength = 1e7;
      hls.config.maxMaxBufferLength = 1e7;
      hls.config.maxBufferSize = 1e9;
    } else {
      hls.config.maxBufferLength = Math.max(60, Math.ceil(30 * speedRef.current));
      hls.config.maxMaxBufferLength = HLS_BASE.maxMaxBufferLength;
      hls.config.maxBufferSize = HLS_BASE.maxBufferSize;
    }
  };

  useEffect(() => {
    const video = ref.current;
    if (!video || !src) return undefined;

    if (isHls && Hls.isSupported()) {
      const hls = new Hls(HLS_BASE);
      hlsRef.current = hls;
      setLevels([]);
      setCurLevel(-1);
      hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
        const lv = (data.levels || []).map((l, i) => ({
          index: i,
          height: l.height || 0,
          label: l.height ? (l.height >= 2000 ? '4K' : `${l.height}p`) : `${Math.round((l.bitrate || 0) / 1e6)}M`,
        }));
        // Only expose a selector when there's a real choice.
        if (lv.length > 1) {
          // Default: best rendition at or below 1080p (fast start); fall back to
          // the lowest if every rendition is bigger.
          const under = lv.filter((l) => l.height && l.height <= 1080);
          const def = (under.length ? under[under.length - 1] : lv[0]).index;
          hls.startLevel = def;
          hls.currentLevel = def;
          setLevels(lv);
          setCurLevel(def);
        }
      });
      hls.on(Hls.Events.LEVEL_SWITCHED, () => {
        // Reflect ABR-driven switches when we're in Auto mode.
        if (hls.autoLevelEnabled) setCurLevel(-1);
      });
      hls.loadSource(src);
      hls.attachMedia(video);
      applyBufferTarget();
      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    }

    // Native HLS (Safari/iOS) or progressive MP4.
    video.src = src;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, type]);

  const pickLevel = (idx) => {
    const hls = hlsRef.current;
    if (hls) {
      hls.currentLevel = idx; // -1 re-enables ABR (Auto)
      hls.loadLevel = idx;
    }
    setCurLevel(idx);
    setQualOpen(false);
  };

  // Keep the chosen playback rate applied across the clip's life. Loading a new
  // source (or recovering an expired URL) resets playbackRate to 1, so we
  // re-assert it on every media (re)load as well as on user change. Also grows
  // the HLS look-ahead so higher speeds get a deeper buffer.
  useEffect(() => {
    speedRef.current = speed;
    const v = ref.current;
    if (v) {
      try {
        v.playbackRate = speed;
      } catch {
        /* some sources briefly reject rate changes; reasserted on load */
      }
    }
    applyBufferTarget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed]);

  useEffect(() => {
    preloadRef.current = preload;
    applyBufferTarget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preload]);

  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const apply = () => {
      try {
        v.playbackRate = speedRef.current;
      } catch {
        /* noop */
      }
    };
    const onProgress = () => {
      const d = v.duration;
      if (!d || !Number.isFinite(d) || !v.buffered || v.buffered.length === 0) {
        setBuffered(0);
        return;
      }
      // Buffered fraction ahead from the current position to the furthest
      // contiguous-ish end (use the last range end as a simple proxy).
      const end = v.buffered.end(v.buffered.length - 1);
      setBuffered(Math.max(0, Math.min(1, end / d)));
    };
    v.addEventListener('loadeddata', apply);
    v.addEventListener('canplay', apply);
    v.addEventListener('play', apply);
    v.addEventListener('progress', onProgress);
    v.addEventListener('timeupdate', onProgress);
    v.addEventListener('durationchange', onProgress);
    return () => {
      v.removeEventListener('loadeddata', apply);
      v.removeEventListener('canplay', apply);
      v.removeEventListener('play', apply);
      v.removeEventListener('progress', onProgress);
      v.removeEventListener('timeupdate', onProgress);
      v.removeEventListener('durationchange', onProgress);
    };
  }, []);

  // Close the speed menu on outside tap / Escape.
  useEffect(() => {
    if (!speedOpen) return undefined;
    const onDown = (e) => {
      if (speedBoxRef.current && !speedBoxRef.current.contains(e.target)) setSpeedOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setSpeedOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [speedOpen]);

  // Close the quality menu on outside tap / Escape.
  useEffect(() => {
    if (!qualOpen) return undefined;
    const onDown = (e) => {
      if (qualBoxRef.current && !qualBoxRef.current.contains(e.target)) setQualOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setQualOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [qualOpen]);

  // Auto-recover an expired presigned MP4 URL without losing the user's place.
  useEffect(() => {
    const video = ref.current;
    if (!video || !getFreshSrc) return undefined;
    if (isHls) return undefined; // public CDN — no expiry to recover from

    let recovering = false;
    let stallTimer = null;

    const recover = async () => {
      if (recovering) return;
      recovering = true;
      const at = video.currentTime;
      const wasPlaying = !video.paused && !video.ended;
      try {
        const fresh = await getFreshSrc();
        if (fresh) {
          const onMeta = () => {
            video.removeEventListener('loadedmetadata', onMeta);
            try {
              if (Number.isFinite(at) && at > 0) video.currentTime = at;
            } catch {
              /* seek may fail until metadata settles; ignore */
            }
            if (wasPlaying) video.play().catch(() => {});
          };
          video.addEventListener('loadedmetadata', onMeta);
          video.src = fresh;
          video.load();
        }
      } catch {
        /* leave the native error surfaced if re-signing also fails */
      } finally {
        // Brief cooldown so a persistent failure can't tight-loop.
        setTimeout(() => {
          recovering = false;
        }, 5000);
      }
    };

    const clearStall = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };
    // A 15s dead stall (not at the end) is abnormal for normal buffering and is
    // the tell-tale of a 403'd range request after the URL expired.
    const armStall = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        if (!video.ended) recover();
      }, 15000);
    };

    const onError = () => recover();

    video.addEventListener('error', onError);
    video.addEventListener('stalled', armStall);
    video.addEventListener('waiting', armStall);
    video.addEventListener('playing', clearStall);
    video.addEventListener('timeupdate', clearStall);
    video.addEventListener('canplay', clearStall);

    return () => {
      clearStall();
      video.removeEventListener('error', onError);
      video.removeEventListener('stalled', armStall);
      video.removeEventListener('waiting', armStall);
      video.removeEventListener('playing', clearStall);
      video.removeEventListener('timeupdate', clearStall);
      video.removeEventListener('canplay', clearStall);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, type, getFreshSrc]);

  // Optional overlay soundtrack (one chosen song). It plays as its own
  // continuous track at NORMAL speed and loops to fill the clip — deliberately
  // NOT synced to the video's clock, so speeding the clip up/down doesn't pitch
  // or chop the music. The clip's own audio is muted while a song plays.
  const hasSong = !!song && (song.source === 'upload' ? !!song.url : !!song.videoId);
  useEffect(() => {
    const v = ref.current;
    if (v) v.muted = hasSong;
  }, [hasSong, src]);

  // Uploaded-MP3 soundtrack: gate play/pause off the video, force 1x, loop.
  useEffect(() => {
    const v = ref.current;
    const a = songAudioRef.current;
    if (!v || !a || !song || song.source !== 'upload' || !song.url) return undefined;
    a.loop = true;
    a.playbackRate = 1;
    a.volume = 1;
    const play = () => {
      a.playbackRate = 1;
      a.play().catch(() => {});
    };
    const pause = () => a.pause();
    const onSeeked = () => {
      // Restart the track when the viewer jumps back to the very start.
      if (v.currentTime < 0.25) {
        try {
          a.currentTime = 0;
        } catch {
          /* noop */
        }
      }
    };
    if (!v.paused && !v.ended) play();
    v.addEventListener('play', play);
    v.addEventListener('pause', pause);
    v.addEventListener('ended', pause);
    v.addEventListener('seeked', onSeeked);
    return () => {
      v.removeEventListener('play', play);
      v.removeEventListener('pause', pause);
      v.removeEventListener('ended', pause);
      v.removeEventListener('seeked', onSeeked);
      a.pause();
    };
  }, [song]);

  // YouTube-sourced soundtrack: same idea via the IFrame API (1x, loop).
  useEffect(() => {
    const v = ref.current;
    if (!v || !song || song.source !== 'youtube' || !song.videoId) return undefined;
    let player;
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !songYtRef.current) return;
      player = new YT.Player(songYtRef.current, {
        videoId: song.videoId,
        playerVars: { controls: 0, disablekb: 1, modestbranding: 1, rel: 0, playsinline: 1, loop: 1, playlist: song.videoId },
        events: {
          onReady: () => {
            try {
              player.setVolume(100);
              player.setPlaybackRate(1);
              if (!v.paused && !v.ended) player.playVideo();
            } catch {
              /* noop */
            }
          },
          onStateChange: (e) => {
            // Loop: when the track ends, jump back and keep going.
            if (e.data === 0) {
              try {
                player.seekTo(0);
                player.playVideo();
              } catch {
                /* noop */
              }
            }
          },
        },
      });
    });
    const play = () => {
      try {
        if (player && player.setPlaybackRate) player.setPlaybackRate(1);
        if (player && player.playVideo) player.playVideo();
      } catch {
        /* noop */
      }
    };
    const pause = () => {
      try {
        if (player && player.pauseVideo) player.pauseVideo();
      } catch {
        /* noop */
      }
    };
    v.addEventListener('play', play);
    v.addEventListener('pause', pause);
    v.addEventListener('ended', pause);
    return () => {
      cancelled = true;
      v.removeEventListener('play', play);
      v.removeEventListener('pause', pause);
      v.removeEventListener('ended', pause);
      try {
        if (player && player.destroy) player.destroy();
      } catch {
        /* noop */
      }
    };
  }, [song]);

  const bufferedPct = Math.round(buffered * 100);
  // High-frame-rate footage (e.g. a RED clip shot at 160 fps on a 24 fps
  // timeline) plays in slow motion; `trueSpeed` is the speed-up that restores
  // real-life motion. Browsers cap playbackRate at 16×.
  const trueX = Number(trueSpeed) > 1.05 ? Math.min(16, Math.round(Number(trueSpeed) * 100) / 100) : 0;
  const atTrueSpeed = trueX > 0 && Math.abs(speed - trueX) < 0.01;

  // While paused, Left/Right (and ,/.) step exactly one frame so review is
  // precise and independent of playback speed. We listen on the window in the
  // capture phase because the browser's built-in timeline scrubber (an <input
  // range> inside the video's shadow DOM) otherwise grabs the arrows first and
  // jumps ~1% of the duration per press. While playing, arrows keep their
  // native seek behavior.
  useEffect(() => {
    const stepFrame = (dir) => {
      const v = ref.current;
      if (!v) return;
      const f = fpsRef.current || 30;
      // Snap to the frame grid, then nudge a quarter-frame into the target frame
      // so the decoder reliably lands on (and renders) that exact frame.
      let frame = Math.round(v.currentTime * f) + dir;
      if (frame < 0) frame = 0;
      let t = frame / f + 1 / (4 * f);
      if (Number.isFinite(v.duration) && v.duration > 0) {
        t = Math.min(t, v.duration - 1 / (2 * f));
      }
      v.currentTime = Math.max(0, t);
    };
    const onKey = (e) => {
      const v = ref.current;
      if (!v) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const back = e.key === 'ArrowLeft' || e.key === ',';
      const fwd = e.key === 'ArrowRight' || e.key === '.';
      if (!back && !fwd) return;
      // Don't hijack arrows while typing in a real field. (The native video
      // scrubber reports the <video> host as activeElement, not an INPUT, so
      // frame-stepping still works when the player has focus.)
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) {
        return;
      }
      if (!v.paused) return; // let the native control seek while playing
      e.preventDefault();
      e.stopPropagation();
      stepFrame(fwd ? 1 : -1);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  return (
    <div className="cs-playerbox">
      <video
        ref={ref}
        className="cs-player"
        controls
        autoPlay
        playsInline
        poster={poster}
        preload="auto"
      />
      {song?.source === 'upload' && song.url && (
        <audio ref={songAudioRef} src={httpsify(song.url)} preload="auto" />
      )}
      {song?.source === 'youtube' && song.videoId && (
        <div style={{ position: 'absolute', left: -99999, top: 0, width: 320, height: 180, pointerEvents: 'none' }}>
          <div ref={songYtRef} />
        </div>
      )}
      <div className="cs-pbar">
        <button
          type="button"
          className={`cs-pbar-btn ${preload ? 'on' : ''}`}
          onClick={() => setPreload((p) => !p)}
          title={
            preload
              ? 'Preloading the whole clip ahead for smooth fast playback & scrubbing'
              : 'Preload the whole clip ahead so high-speed playback / scrubbing won’t stall'
          }
        >
          {preload ? `⏬ ${bufferedPct}%` : '⏬ Preload'}
        </button>
        {levels.length > 1 && (
          <div className="cs-speed" ref={qualBoxRef}>
            <button
              type="button"
              className="cs-pbar-btn"
              onClick={() => setQualOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={qualOpen}
              title="Video quality (defaults to 1080p for a fast start)"
            >
              {curLevel < 0 ? 'Auto' : levels.find((l) => l.index === curLevel)?.label || 'Auto'}
              <span className="cs-speed-caret">▾</span>
            </button>
            {qualOpen && (
              <ul className="cs-speed-menu" role="listbox" aria-label="Video quality">
                <li>
                  <button type="button" role="option" aria-selected={curLevel < 0} className={curLevel < 0 ? 'on' : ''} onClick={() => pickLevel(-1)}>
                    Auto
                  </button>
                </li>
                {levels
                  .slice()
                  .reverse()
                  .map((l) => (
                    <li key={l.index}>
                      <button type="button" role="option" aria-selected={curLevel === l.index} className={curLevel === l.index ? 'on' : ''} onClick={() => pickLevel(l.index)}>
                        {l.label}
                        {l.height >= 2000 ? ' · slower' : ''}
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
        <div className="cs-speed" ref={speedBoxRef}>
          <button
            type="button"
            className="cs-pbar-btn"
            onClick={() => setSpeedOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={speedOpen}
            title={trueX ? `Playback speed — this clip is slow motion; ${trueX}× is real-life speed` : 'Playback speed'}
          >
            {atTrueSpeed ? `${fmtSpeed(speed)}× · real time` : `${fmtSpeed(speed)}×`} <span className="cs-speed-caret">▾</span>
          </button>
          {speedOpen && (
            <ul className="cs-speed-menu" role="listbox" aria-label="Playback speed">
              {trueX > 0 && (
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={atTrueSpeed}
                    className={`cs-speed-true ${atTrueSpeed ? 'on' : ''}`}
                    title="Play at the clip's real-life speed (cancels the in-camera slow motion)"
                    onClick={() => {
                      setSpeed(trueX);
                      setSpeedOpen(false);
                    }}
                  >
                    ⚡ Real time · {fmtSpeed(trueX)}×
                  </button>
                </li>
              )}
              {SPEEDS.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={s === speed}
                    className={s === speed ? 'on' : ''}
                    onClick={() => {
                      setSpeed(s);
                      setSpeedOpen(false);
                    }}
                  >
                    {fmtSpeed(s)}×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
